<template>
  <div>
    <!-- 页面 Loading -->
    <div v-if="isPageLoading" class="page-loading">
      <van-loading type="spinner" color="#17B663" />
    </div>

    <van-nav-bar :title="t('newUsers.title')" class="custom-nav-bar" :border="false">
      <template #left>
        <Button type="back" @click="onClickLeft" />
      </template>
    </van-nav-bar>
    <div class="content" v-show="!isPageLoading">
      <div class="relative w-full  flex flex-col items-center pt-[100px]">
        <img :src="getAssetsFile('activity/lh.png')" alt=""
          class="w-[200px] h-[200px] absolute top-30px left-[40%] z-[1]">
        <div class="zhuanpan-container">
          <div class="zhuanpan" :class="{ 'spinning': isSpinning }" @click="spinWheel">
            <!-- 转盘区域金额显示 -->
            <div class="wheel-segments">
              <div v-for="(segment, index) in wheelSegments" :key="index" class="wheel-segment"
                :class="`segment-${index}`">
                <div class="segment-content">
                  <!-- 显示金额或文字 -->
                  <div v-if="segment.reward && segment.reward.type === 1" class="segment-amount random-amount">
                    <!-- 随机金额不显示具体数值，只显示图标 -->
                  </div>
                  <div v-else-if="segment.reward && segment.reward.type === 2" class="segment-amount fixed-amount">
                    {{ VITE_symbol }} {{ numberFormat(segment.reward.amount, { minDecimals: 2 }) }}
                  </div>
                  <div v-else-if="segment.reward && segment.reward.type === 3" class="segment-amount bonus-amount">
                    {{ VITE_symbol }} {{ numberFormat(segment.reward.amount, { minDecimals: 2 }) }}
                  </div>
                  <div v-else-if="segment.reward && segment.reward.type === 0" class="segment-text empty-text">
                    Thanks!
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="zhizhen">GO</div>
        </div>
      </div>
      <div class="teerser">
        <div class="text-[13px]">{{ t('newUsers.rulesTitle') }}</div>
        <p class="text-[11px] mt-[15px] lh-[13px]">{{ t('newUsers.rule1') }}</p>
        <p class="text-[11px] mt-[15px] lh-[13px]">{{ t('newUsers.rule2') }}</p>
        <p class="text-[11px]  mt-[15px] lh-[13px]">{{ t('newUsers.rule3') }}</p>
        <p class="text-[11px]  mt-[15px] lh-[13px]">{{ t('newUsers.rule4') }}</p>
        <p class="text-[11px]  mt-[15px] lh-[13px]">{{ t('newUsers.rule5') }}</p>
      </div>
    </div>

    <!-- 奖励弹窗 -->
    <RewardPopup :show="showRewardPopup" @close="closeRewardPopup" />
  </div>
</template>
<script setup lang="ts">
import { Button } from "@/components/Button"
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ref, onMounted } from 'vue'
import * as GameApi from '@/api/game'
import RewardPopup from '@/components/RewardPopup.vue'
import { useRewardStore } from '@/stores/modules/reward'
import { useUserStore } from '@/stores/modules/user'
import { numberFormat } from '@/utils/formatter'
import { getAssetsFile } from '@/utils/pub-use'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const onClickLeft = () => {
  // 如果正在抽奖动画中或弹窗显示中，禁止返回
  if (isSpinning.value || showRewardPopup.value) {
    return
  }

  // 设置跳过标记，防止回到首页时再次跳转到抽奖页面
  const userId = userStore.userInfo?.id
  if (userId) {
    const skipTurntableKey = `skip_turntable_${userId}`
    sessionStorage.setItem(skipTurntableKey, 'true')
    console.log('设置跳过抽奖页面标记:', skipTurntableKey)
  }

  router.back()
}
const userTurntableData = ref<any>({})
const userStore = useUserStore()

// 获取货币符号
const VITE_symbol = import.meta.env.VITE_SYMBOL || '₱'

// 页面加载状态
const isPageLoading = ref(true)

// 转盘状态
const isSpinning = ref(false)

// 奖励弹窗状态
const showRewardPopup = ref(false)

// 获取reward store
const rewardStore = useRewardStore()

// 转盘区域配置 - 8个区域，按顺时针方向从12点开始
const wheelSegments = ref([
  { index: 0, angle: 0, reward: null, type: 'random' },      // 12点 - 随机奖励1
  { index: 1, angle: 45, reward: null, type: 'fixed' },     // 1:30点 - 固定奖励1
  { index: 2, angle: 90, reward: null, type: 'empty' },     // 3点 - Thanks!
  { index: 3, angle: 135, reward: null, type: 'fixed' },    // 4:30点 - 固定奖励2
  { index: 4, angle: 180, reward: null, type: 'random' },   // 6点 - 随机奖励2
  { index: 5, angle: 225, reward: null, type: 'bonus' },    // 7:30点 - Bonus奖励
  { index: 6, angle: 270, reward: null, type: 'fixed' },    // 9点 - 固定奖励3
  { index: 7, angle: 315, reward: null, type: 'fixed' }     // 10:30点 - 固定奖励4
])

onMounted(() => {
  preloadImages()
  userTurntable()
})

// 预加载图片
const preloadImages = () => {
  const images = [
    getAssetsFile('activity/lh.png'),
    getAssetsFile('activity/bg.png'),
    getAssetsFile('activity/zp.png'),
    getAssetsFile('activity/zhizhen.png'),
    getAssetsFile('activity/we.png')
  ]

  let loadedCount = 0
  const totalImages = images.length

  const checkAllLoaded = () => {
    loadedCount++
    if (loadedCount >= totalImages) {
      // 所有图片加载完成，延迟一点时间让用户看到loading效果
      setTimeout(() => {
        isPageLoading.value = false
      }, 500)
    }
  }

  images.forEach(src => {
    const img = new Image()
    img.onload = checkAllLoaded
    img.onerror = checkAllLoaded // 即使加载失败也继续
    img.src = src
  })
}

const userTurntable = async () => {
  try {
    const response = await GameApi.userTurntable()
    console.log('转盘数据:', response.data)

    // 检查 data 是否为 null
    if (response.data === null) {
      console.log('转盘数据为null，重置奖励数据')
      // 如果 data 为 null，重置所有数据
      userTurntableData.value = {}
      // 重置转盘区域
      wheelSegments.value = wheelSegments.value.map(segment => ({
        ...segment,
        reward: null
      }))
      return
    }

    userTurntableData.value = response.data

    // 初始化转盘区域奖励数据
    initWheelSegments()
  } catch (error) {
    console.error('获取转盘数据失败:', error)
    // 请求失败时也重置数据
    userTurntableData.value = {}
    wheelSegments.value = wheelSegments.value.map(segment => ({
      ...segment,
      reward: null
    }))
  }
}

// 初始化转盘区域奖励数据
const initWheelSegments = () => {
  const rewardList = userTurntableData.value.rewardInfoList || []

  // 按权重排序奖励
  const sortedRewards = rewardList.sort((a, b) => b.weight - a.weight)

  // 分配奖励到转盘区域
  let rewardIndex = 0

  // 12点位置 - 第一个随机奖励
  if (sortedRewards[rewardIndex] && sortedRewards[rewardIndex].type === 1) {
    wheelSegments.value[0].reward = sortedRewards[rewardIndex]
    rewardIndex++
  }

  // 6点位置 - 第二个随机奖励
  if (sortedRewards[rewardIndex] && sortedRewards[rewardIndex].type === 1) {
    wheelSegments.value[4].reward = sortedRewards[rewardIndex]
    rewardIndex++
  }

  // 7:30点位置 - Bonus奖励（最大的固定金额）
  const bonusReward = sortedRewards.find(r => r.type === 3)
  if (bonusReward) {
    wheelSegments.value[5].reward = bonusReward
  }

  // 固定奖励按金额大小排序
  const fixedRewards = sortedRewards.filter(r => r.type === 2)
  const sortedFixedRewards = fixedRewards.sort((a, b) => b.amount - a.amount) // 按金额从大到小排序

  // 按你的要求分配固定奖励：
  // 10:30点位置 - 最小金额
  if (sortedFixedRewards[3]) {
    wheelSegments.value[7].reward = sortedFixedRewards[3]
  }

  // 1:30点位置 - 倒数第二小金额
  if (sortedFixedRewards[2]) {
    wheelSegments.value[1].reward = sortedFixedRewards[2]
  }

  // 4:30点位置 - 倒数第三大金额
  if (sortedFixedRewards[1]) {
    wheelSegments.value[3].reward = sortedFixedRewards[1]
  }

  // 9点位置 - 倒数第四大金额（第二大）
  if (sortedFixedRewards[0]) {
    wheelSegments.value[6].reward = sortedFixedRewards[0]
  }

  // 2号位置（3点）设为"Thanks!"
  wheelSegments.value[2].reward = { type: 0, amount: 0, description: 'Thanks!' }

  console.log('转盘区域配置:', wheelSegments.value)
}

// 转盘旋转函数
const spinWheel = async () => {
  if (isSpinning.value) return // 如果正在旋转，不执行

  // 检查是否已登录
  if (!userStore.isLoggedIn || !userStore.userInfo || !userStore.userInfo.userId) {
    // 未登录，显示全局注册弹窗
    const inviteCode = (route.query?.pid as string) || "";
    window.$showRegisterDialog(inviteCode);
    return;
  }

  isSpinning.value = true

  try {
    // 调用抽奖接口
    const response = await GameApi.lottery()
    console.log('抽奖结果:', response)

    // 获取中奖数据
    const { reward, countDown } = response.data
    console.log('抽奖API返回的数据:', response.data)
    console.log('reward:', reward)
    console.log('countDown:', countDown)
    console.log('reward类型:', typeof reward, 'reward值:', reward)

    // 查找匹配的固定金额奖励
    let targetSegmentIndex = -1
    let matchedReward = null

    // 遍历转盘区域，查找匹配的固定金额
    for (let i = 0; i < wheelSegments.value.length; i++) {
      const segment = wheelSegments.value[i]
      if (segment.reward &&
        (segment.reward.type === 2 || segment.reward.type === 3) &&
        segment.reward.amount === reward) {
        targetSegmentIndex = i
        matchedReward = segment.reward
        break
      }
    }

    // 如果没有匹配到固定金额，指向随机奖励区域
    if (targetSegmentIndex === -1) {
      // 查找随机奖励区域
      for (let i = 0; i < wheelSegments.value.length; i++) {
        const segment = wheelSegments.value[i]
        if (segment.reward && segment.reward.type === 1) {
          targetSegmentIndex = i
          matchedReward = { type: 1, amount: reward, minAmount: 0, maxAmount: 0 }
          break
        }
      }
    }

    // 如果还是没找到，默认指向第一个区域
    if (targetSegmentIndex === -1) {
      targetSegmentIndex = 0
      matchedReward = { type: 1, amount: reward }
    }

    console.log('目标区域:', targetSegmentIndex, '匹配奖励:', matchedReward)

    // 转盘有8个扇形，每个扇形45度
    const segmentAngle = 45

    // 计算目标角度：让指针框住12点钟方向的扇形
    const targetAngle = -(targetSegmentIndex * segmentAngle)

    // 转盘总旋转角度：多转几圈 + 目标角度
    const totalRotation = 1440 + targetAngle // 4圈 + 目标角度

    // 使用 requestAnimationFrame 实现平滑旋转
    const startAngle = 0
    const endAngle = totalRotation
    const duration = 3000 // 3秒动画
    const startTime = Date.now()

    const animate = () => {
      const currentTime = Date.now()
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // 使用更自然的缓动函数 - 开始慢，结束慢
      const easeOutCubic = 1 - Math.pow(1 - progress, 3)
      const currentAngle = startAngle + (endAngle - startAngle) * easeOutCubic

      // 获取转盘元素并设置旋转角度
      const wheel = document.querySelector('.zhuanpan') as HTMLElement
      if (wheel) {
        wheel.style.transform = `rotate(${currentAngle}deg)`
      }

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // 旋转结束
        isSpinning.value = false
        console.log('抽奖完成，指向扇形:', targetSegmentIndex, '角度:', targetAngle)

        // 显示中奖结果弹窗
        showRewardResult({ reward: 0, rewardBound: matchedReward?.amount }, countDown)
      }
    }

    animate()

  } catch (error) {
    console.error('抽奖失败:', error)
    isSpinning.value = false
    // 可以显示错误提示
  }
}

// 显示中奖结果
const showRewardResult = (reward: { reward: number, rewardBound: number }, countDown) => {
  console.log('showRewardResult 接收到的参数:', { reward, countDown })
  console.log('reward.amount:', reward)
  console.log('countDown:', countDown)

  // 确保 amount 是有效数字
  const validAmount = reward
  const validCountDown = countDown || 0

  console.log('有效金额:', validAmount, '有效倒计时:', validCountDown)

  // 将奖励数据存储到store
  rewardStore.setRewardData(validAmount, validCountDown)

  console.log('设置后的 store 数据:', {
    rewardAmount: rewardStore.rewardAmount,
    countdownSeconds: rewardStore.countdownSeconds
  })

  // 验证localStorage中的数据
  const savedData = localStorage.getItem('rewardData')
  console.log('localStorage中的数据:', savedData)

  // 更新新用户转盘状态为已领取，但保留当前的奖励数据
  rewardStore.setNewUserExclusiveData({
    ...userTurntableData.value,
    status: 1, // 设置为已领取状态
    rewardAmount: validAmount.rewardBound, // 保留当前奖励金额
    expiredTime: validCountDown // 保留当前倒计时
  })

  // 设置抽奖完成标记到 localStorage
  const userId = userStore.userInfo?.id
  if (userId) {
    const turntableCompletedKey = `turntable_completed_${userId}`
    localStorage.setItem(turntableCompletedKey, 'true')
    console.log('设置抽奖完成标记:', turntableCompletedKey)
  }

  // 延迟显示奖励弹窗，确保数据已保存
  setTimeout(() => {
    console.log('准备显示弹窗，最终localStorage数据:', localStorage.getItem('rewardData'))
    showRewardPopup.value = true
  }, 100)
}

// 关闭奖励弹窗
const closeRewardPopup = () => {
  showRewardPopup.value = false

  // 确保抽奖完成标记已设置
  const userId = userStore.userInfo?.id
  if (userId) {
    const turntableCompletedKey = `turntable_completed_${userId}`
    localStorage.setItem(turntableCompletedKey, 'true')
    // 清除跳过标记，因为用户已经完成抽奖
    const skipTurntableKey = `skip_turntable_${userId}`
    sessionStorage.removeItem(skipTurntableKey)
    // 设置一个标记，表示刚完成抽奖返回首页，暂时跳过检查
    sessionStorage.setItem('just_completed_turntable', 'true')
    console.log('抽奖完成，已设置完成标记并清除跳过标记')
  }

  // 关闭弹窗后自动返回首页
  router.push('/')
}
</script>
<style lang="scss" scoped>
.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px -2px 22px 0px rgba(53, 189, 128, 0.61);

  &>* {
    height: 100%;
  }
}

.content {
  background: getPath('activity/bg.png') no-repeat center center;
  background-size: 100% 100%;
  width: 100vw;
  min-height: 80vh;
}

.zhuanpan-container {
  position: relative;
  width: 310px;
  height: 310px;
  /* 改为和转盘一样的尺寸 */
  display: flex;
  justify-content: center;
  align-items: center;
}

.zhuanpan {
  background: getPath('activity/zp.png') no-repeat center center;
  background-size: 100% 100%;
  width: 310px;
  height: 310px;
  z-index: 1;
  cursor: pointer;
  /* 设置旋转中心点 */
  transform-origin: center center;
  /* 强制硬件加速，防止摇晃 */
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  /* 确保转盘是完美的圆形 */
  border-radius: 50%;
  /* 移除所有 transition，让 JavaScript 完全控制 */
  position: relative;
}

.zhuanpan.spinning {
  pointer-events: none;
  /* 旋转时禁用点击 */
}

.zhuanpan.spinning:hover {
  transform: none;
}

/* 转盘区域样式 */
.wheel-segments {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
}

.wheel-segment {
  position: absolute;
  width: 50%;
  height: 50%;
  top: 50%;
  left: 50%;
  transform-origin: 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.segment-content {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* 重新调整各区域定位 - 增加与图片的间距 */
.segment-0 {
  /* 12点钟方向 (0度) - 顶部 - 随机奖励1 */
  transform: translate(-50%, -50%) rotate(0deg) translateY(-100px) rotate(0deg);
}

.segment-1 {
  /* 1点半方向 (45度) - 右上 - 固定奖励1 */
  transform: translate(30%, -100%) rotate(75deg) translateY(-25px) rotate(-20deg);
}

.segment-2 {
  /* 3点钟方向 (90度) - 右侧 - Thanks! */
  transform: translate(30%, -49%) rotate(90deg) translateY(-100px) rotate(0deg);
}

.segment-3 {
  /* 4点半方向 (135度) - 右下 - 固定奖励2 */
  transform: translate(20%, -50%) rotate(135deg) translateY(-180px) rotate(-360deg);
}

.segment-4 {
  /* 6点钟方向 (180度) - 底部 - 随机奖励2 */
  transform: translate(-50%, -50%) rotate(180deg) translateY(-100px) rotate(-180deg);
}

.segment-5 {
  /* 7点半方向 (225度) - 左下 - Bonus奖励 */
  transform: translate(-17%, 99%) rotate(250deg) translateY(-25px) rotate(-30deg);
}

.segment-6 {
  /* 9点钟方向 (270度) - 左侧 - 固定奖励3 */
  transform: translate(-38%, 50%) rotate(270deg) translateY(-100px) rotate(-0deg);
}

.segment-7 {
  /* 10点半方向 (315度) - 左上 - 固定奖励4 */
  transform: translate(-73%, -35%) rotate(265deg) translateY(-50px) rotate(-315deg);
}

/* 金额显示样式 - 增加倾斜效果和间距 */
.segment-amount {
  font-size: 10px;
  font-weight: bold;
  text-align: center;
  color: #7D3B17;
  white-space: nowrap;
  font-weight: bold;
  margin-top: 15px;
}

.segment-text {
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  color: #7D3B17;
  font-family: sans-serif;
  white-space: nowrap;
  /* 增加与图片的间距 */
  margin-top: 15px;
}

/* 不同类型奖励的颜色 */
.random-amount {
  color: #7D3B17;
  /* 黑色 */
}

.fixed-amount {
  color: #7D3B17;
  /* 黑色 */
}

.bonus-amount {
  color: #7D3B17;
  /* 黑色 */
}

.empty-text {
  color: #7D3B17;
  /* 黑色 */
}

.zhizhen {
  background: getPath('activity/zhizhen.png') no-repeat center center;
  background-size: 100% 100%;
  width: 120px;
  height: 210px;
  position: absolute;
  top: 33%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #FFFFFF;
  font-size: 25px;
  font-weight: bold;
  text-align: center;
  line-height: 320px;
  letter-spacing: 1px;
  z-index: 2;
  pointer-events: none;
  /* 指针不响应点击事件 */
}

.teerser {
  background: getPath('activity/we.png') no-repeat center center;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 40px;
  height: 200px;
  border-radius: 8px;
  padding: 10px;
  font-size: 13px;
  color: #50b388;

}

.teerser p {
  line-height: 1.5em;
  letter-spacing: 0.025em;
}

/* 页面 Loading 样式 */
.page-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
